import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { getCustomerPortalLaunchReadinessSummary } from '../api/launchReadinessApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { useCustomerPortal } from '../portal/CustomerPortalContext';
import {
  filterSafeReadinessWarnings,
  formatSafeAccountMessage,
} from '../utils/customerPortalLabels';
import { DetailField } from './InfoCard';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';

const { semantic } = colorTokens;

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2.5,
};

const mutedSx = {
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.muted,
};

export function CustomerLaunchReadinessBlock({ tenantId }) {
  const { t } = useTranslation(['launchReadiness', 'common', 'customerPortal', 'customers']);
  const { activeCustomerId } = useCustomerPortal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!activeCustomerId || !tenantId) {
      return;
    }
    setLoading(true);
    try {
      const response = await getCustomerPortalLaunchReadinessSummary(activeCustomerId, tenantId);
      setData(response);
    } finally {
      setLoading(false);
    }
  }, [activeCustomerId, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <LoadingSkeleton type="Detail" sx={{ mb: 0 }} />;
  }

  if (!data) {
    return null;
  }

  const smsEnabled = data.sms_notifications_enabled === true;
  const safeWarnings = filterSafeReadinessWarnings(
    data.safe_warnings?.length ? data.safe_warnings : (data.warnings || []),
    { smsEnabled },
  );
  const accountMessage = formatSafeAccountMessage(
    data.safe_message_key,
    data.safe_message || data.message,
  );

  const billingLabel = data.billing_status === 'ok'
    ? t('launchReadiness:billingOk')
    : t('launchReadiness:billingPending');

  const setupLabel = data.setup_status === 'ready'
    ? t('launchReadiness:modulesReady')
    : t('launchReadiness:setupInProgress');

  const modulesLabel = data.modules_status === 'ready'
    ? t('launchReadiness:modulesReady')
    : t('launchReadiness:setupInProgress');

  const accountReady = data.account_ready === true
    || data.safe_message_key === 'accountReady'
    || data.launch_ready === true;

  const accountLabel = accountReady
    ? t('customerPortal:safeMessages.accountReady')
    : t('customerPortal:safeMessages.accountNeedsAttentionBeforeLaunch');

  return (
    <Box sx={{ width: '100%' }}>
      <SectionCard
        variant="Default"
        title={t('customerPortal:sms.sectionTitle')}
        sx={{ mb: 2.5 }}
      >
        <Box sx={detailGridSx}>
          <DetailField label={t('common:status')}>
            <StatusBadge
              status={smsEnabled ? 'ready' : 'pending'}
              category="generic"
              apiLabel={smsEnabled
                ? t('customers:portal.sms_active_short')
                : t('customers:portal.sms_inactive_short')}
              size="Sm"
              tone={smsEnabled ? 'Success' : 'Neutral'}
              sx={{ alignSelf: 'flex-start' }}
            />
          </DetailField>
        </Box>
        <Typography sx={{ ...mutedSx, mt: 1.5 }}>
          {smsEnabled
            ? t('customerPortal:sms.enabledUsageBased')
            : t('customerPortal:sms.disabled')}
        </Typography>
        {smsEnabled && data.sms_activation_deferred && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('customerPortal:sms.activationDeferred')}
          </Alert>
        )}
      </SectionCard>

      <SectionCard
        variant="Default"
        title={t('customerPortal:sections.accountReadiness')}
        sx={{ mb: 0 }}
      >
        <Box sx={detailGridSx}>
          <DetailField label={t('launchReadiness:categoryBilling')}>
            <StatusBadge
              status={data.billing_status || 'pending'}
              category="billing_readiness"
              apiLabel={billingLabel}
              size="Sm"
              sx={{ alignSelf: 'flex-start' }}
            />
          </DetailField>
          <DetailField label={t('launchReadiness:categorySetup')}>
            <StatusBadge
              status={data.setup_status || 'pending'}
              category="generic"
              apiLabel={setupLabel}
              size="Sm"
              tone={data.setup_status === 'ready' ? 'Success' : 'Warning'}
              sx={{ alignSelf: 'flex-start' }}
            />
          </DetailField>
          <DetailField label={t('launchReadiness:categoryModules')}>
            <StatusBadge
              status={data.modules_status || 'pending'}
              category="generic"
              apiLabel={modulesLabel}
              size="Sm"
              tone={data.modules_status === 'ready' ? 'Success' : 'Warning'}
              sx={{ alignSelf: 'flex-start' }}
            />
          </DetailField>
          <DetailField label={t('customerPortal:sections.accountReadiness')}>
            <StatusBadge
              status={accountReady ? 'ready' : 'attention'}
              category="generic"
              apiLabel={accountLabel}
              size="Sm"
              tone={accountReady ? 'Success' : 'Warning'}
              sx={{
                alignSelf: 'flex-start',
                maxWidth: '100%',
                whiteSpace: 'normal',
                height: 'auto',
                py: 0.75,
              }}
            />
          </DetailField>
        </Box>

        {data.support_active && (
          <Typography
            sx={{
              mt: 2,
              fontFamily: typographyTokens.fontFamily,
              fontSize: 13,
              lineHeight: '18px',
              color: semantic.text.secondary,
            }}
          >
            {t('customerPortal:safeMessages.supportModuleActive')}
          </Typography>
        )}

        {safeWarnings.length > 0 && (
          <Stack spacing={1} sx={{ mt: 2.5 }}>
            {safeWarnings.map((warning) => (
              <Alert key={warning} severity="warning">
                {warning}
              </Alert>
            ))}
          </Stack>
        )}

        {accountMessage ? (
          <Alert severity="info" sx={{ mt: 2.5 }}>
            {accountMessage}
          </Alert>
        ) : null}
      </SectionCard>
    </Box>
  );
}
